/**
 * How many handles' names the page keeps, the oldest last hit dropped past it.
 */
export const KILLER_NAMES_HELD = 32;

/**
 * The names of those whose hits landed on the player's character (P2.5-T2, ADR-079's amendment of 2026-10-03, item 5),
 * so a death whose killer has left the view is still named: a poisoner may walk out of view in the ticks its poison
 * runs, and ADR-067's names travel only with the view. The server names a periodic kill's source only while the spawn
 * that applied it stands, and a handle is not reused while its spawn stands, so the name kept from the hit that landed
 * the poison is the killer's.
 */
export class KillerNames {
  private readonly names = new Map<number, string>();

  /**
   * How many handles have a name kept.
   */
  public get size(): number {
    return this.names.size;
  }

  /**
   * Keeps the name of a hit's source as the view held it when the hit arrived, the newest kept last; a hit whose
   * source the page names no one by leaves what was kept, since a poisoner out of view lands its ticks so.
   *
   * @param handle The hit's source.
   * @param name The name the view holds for it, or null when it holds none.
   */
  public hitBy(handle: number, name: string | null): void {
    if (name === null) {
      return;
    }
    this.names.delete(handle);
    this.names.set(handle, name);
    if (this.names.size > KILLER_NAMES_HELD) {
      const oldest = this.names.keys().next();
      if (oldest.done !== true) {
        this.names.delete(oldest.value);
      }
    }
  }

  /**
   * The name kept for a handle.
   *
   * @param handle The handle.
   * @returns The name, or null when no hit from it left one.
   */
  public nameOf(handle: number): string | null {
    return this.names.get(handle) ?? null;
  }

  /**
   * Forgets every name, as a session ends.
   */
  public reset(): void {
    this.names.clear();
  }
}
